/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  Z4ThemeProvider, FetchSWRConfig, notistackConfig, materialUiConfirmConfig, initZ4Rum,
} from '@marageti/z4-lib';
import { SnackbarProvider, useSnackbar, SnackbarKey } from 'notistack';
import { ConfirmProvider, ConfirmProviderProps } from 'material-ui-confirm';
import { CssBaseline, Button } from '@mui/material';
import './assets/sass/reset.scss';
import Router from './routes';
import { themeOptions } from './theme';
import config from './config';
import { AuthProvider } from './store/contexts/auth';
import ChatwayBootstrap from './components/chatway/chatway-bootstrap';
import injectGTM from './utils/gtm/injectGTM';
import { setZ4RumInstance } from './utils/rum';
import ErrorBoundary from './components/error-boundary/ErrorBoundaryComponent';

injectGTM(config.GTM_ID);

const isLocalDev = process.env.NODE_ENV !== 'production';
const buildVersion = process.env.REACT_APP_BUILD_VERSION || '0.0.0';

const rum = !isLocalDev
  ? initZ4Rum({
    appMonitorId: config.RUM_APP_MONITOR_ID,
    identityPoolId: config.RUM_IDENTITY_POOL_ID,
    environment: config.RUM_ENVIRONMENT,
    appVersion: buildVersion,
    releaseId: buildVersion,
  })
  : null;
setZ4RumInstance(rum ?? null);

const SnackbarOkayButton = ({ snackbarKey }: { snackbarKey: SnackbarKey }) => {
  const { closeSnackbar } = useSnackbar();

  const handleClick = () => closeSnackbar(snackbarKey);

  return (
    <Button color="inherit" onClick={handleClick}>
      Ok
    </Button>
  );
};

const defaultDismissAction = (key: SnackbarKey) => (
  <SnackbarOkayButton snackbarKey={key} />
);

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <ErrorBoundary>
    <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID}>
      <Z4ThemeProvider themeOptions={themeOptions}>
        <FetchSWRConfig value={{ revalidateOnFocus: false, revalidateOnReconnect: false }}>
          <SnackbarProvider
            Components={notistackConfig.Components}
            autoHideDuration={notistackConfig.autoHideDuration}
            hideIconVariant={notistackConfig.hideIconVariant}
            action={defaultDismissAction}
          >
            <ConfirmProvider
              defaultOptions={{
                ...materialUiConfirmConfig,
              } as ConfirmProviderProps['defaultOptions']}
            >
              <AuthProvider>
                <ChatwayBootstrap />
                <Router />
                <CssBaseline />
              </AuthProvider>
            </ConfirmProvider>
          </SnackbarProvider>
        </FetchSWRConfig>
      </Z4ThemeProvider>
    </GoogleOAuthProvider>
  </ErrorBoundary>,
);
